import { useState } from "react";
import { Sidebar } from "@/components/layout/Sidebar";
import { useAuth } from "@/hooks/use-auth";
import { useInviteCodes, useUserInvites, useUsuarios } from "@/hooks/use-data";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Plus,
  Copy,
  CheckCircle,
  Link as LinkIcon,
  TicketPlus,
  Users,
  Building2,
  UserPlus,
  Mail,
  Send,
  Trash2,
} from "lucide-react";
import type { InviteCode, UserInvite } from "@shared/schema";

const ROLE_LABELS: Record<string, string> = {
  tv: "Técnico Vistoriador (TV)",
  rt: "Responsável Técnico (RT)",
};

export default function Convites() {
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const empresaId = profile?.empresaId;
  const { data: inviteCodes, isLoading: loadingCodes } = useInviteCodes(empresaId);
  const { data: userInvites, isLoading: loadingUserInvites } = useUserInvites();
  const { data: usuarios } = useUsuarios(empresaId);
  const isOwner = profile?.roles?.includes("owner");

  const hasRT = usuarios?.some((user) => user.roles?.includes("rt")) ?? false;

  // Company invite state
  const [tipo, setTipo] = useState<"PARCEIRO_DE_NEGOCIOS" | "VISTORIADORA">("PARCEIRO_DE_NEGOCIOS");
  const [maxUsos, setMaxUsos] = useState("");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // User invite state
  const [userRole, setUserRole] = useState<"tv" | "rt">("tv");
  const [userEmail, setUserEmail] = useState("");
  const [copiedUserCode, setCopiedUserCode] = useState<string | null>(null);
  const [emailingId, setEmailingId] = useState<string | null>(null);
  const [emailTarget, setEmailTarget] = useState<Record<string, string>>({});

  const createCodeMutation = useMutation({
    mutationFn: async (data: { tipo: string; maxUsos?: number }) => {
      const res = await fetch("/api/invite-codes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar código");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invite-codes"] });
      setMaxUsos("");
      toast({ title: "Código criado", description: "Código de convite gerado com sucesso." });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const createUserInviteMutation = useMutation({
    mutationFn: async (data: { roles: string[]; email?: string }) => {
      const res = await fetch("/api/user-invites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar convite");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-invites"] });
      setUserEmail("");
      toast({ title: "Convite gerado", description: "Convite de usuário criado com sucesso." });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const sendEmailMutation = useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => {
      const res = await fetch(`/api/user-invites/${id}/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao enviar email");
      }
      return res.json();
    },
    onSuccess: (_, { id }) => {
      setEmailingId(null);
      setEmailTarget((prev) => ({ ...prev, [id]: "" }));
      toast({ title: "Email enviado!", description: "O convite foi enviado por email." });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao enviar email", description: err.message, variant: "destructive" });
    },
  });

  const deleteUserInviteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/user-invites/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao excluir convite");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user-invites"] });
      toast({ title: "Convite excluído", description: "O convite foi removido." });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const deleteCodeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/invite-codes/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao excluir código");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invite-codes"] });
      toast({ title: "Código excluído", description: "O código foi removido." });
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const getRegistrationLink = (code: string) => `${window.location.origin}/registrar?code=${code}`;
  const getUserInviteLink = (code: string) => `${window.location.origin}/entrar?invite=${code}`;

  const copyText = async (text: string, key: string, setter: (k: string | null) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(key);
      toast({ title: "Copiado!", description: "Texto copiado para a área de transferência." });
      setTimeout(() => setter(null), 3000);
    } catch {
      toast({ title: "Erro", description: "Não foi possível copiar.", variant: "destructive" });
    }
  };

  const roles = profile?.roles || [];
  const canCreate = roles.includes("admin") || roles.includes("rt") || roles.includes("owner");

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 lg:ml-72">
        <div className="p-6 lg:p-8 max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-display font-bold text-slate-900">
              Códigos de Convite
            </h1>
            <p className="text-muted-foreground mt-1">
              Gere convites para parceiros de negócio ou para membros da sua equipe.
            </p>
          </div>

          <Tabs defaultValue="usuarios">
            <TabsList className="mb-6">
              <TabsTrigger value="usuarios" className="gap-2">
                <UserPlus className="w-4 h-4" /> Convidar TV/RT
              </TabsTrigger>
              <TabsTrigger value="empresas" className="gap-2">
                <Building2 className="w-4 h-4" /> Empresas
              </TabsTrigger>
            </TabsList>

            {/* ─── Tab: Convidar Usuários TV/RT ─────────────────────────── */}
            <TabsContent value="usuarios">
              {canCreate && (
                <Card className="mb-8 border-none shadow-md">
                  <CardContent className="p-6">
                    <h2 className="text-lg font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-primary" />
                      Gerar Convite de Usuário
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-4 items-end flex-wrap">
                      <div className="flex-1 space-y-2">
                        <Label>Perfil</Label>
                        <Select value={userRole} onValueChange={(v) => setUserRole(v as "tv" | "rt")}>
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tv">Técnico Vistoriador (TV)</SelectItem>
                            <SelectItem value="rt" disabled={hasRT}>
                              Responsável Técnico (RT)
                              {hasRT && " - Máximo 1 por empresa"}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {userRole === "rt" && hasRT && (
                          <p className="text-sm text-red-600">
                            Esta empresa já possui um Responsável Técnico. Somente 1 RT é permitido por empresa.
                          </p>
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <Label>Email do convidado (opcional)</Label>
                        <Input
                          type="email"
                          placeholder="email@exemplo.com"
                          value={userEmail}
                          onChange={(e) => setUserEmail(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                      <Button
                        onClick={() =>
                          createUserInviteMutation.mutate({
                            roles: [userRole],
                            email: userEmail || undefined,
                          })
                        }
                        disabled={createUserInviteMutation.isPending || (userRole === "rt" && hasRT)}
                        className="gap-2"
                        title={userRole === "rt" && hasRT ? "Esta empresa já possui um Responsável Técnico" : ""}
                      >
                        {createUserInviteMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Gerar Convite
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {loadingUserInvites ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : !userInvites || userInvites.length === 0 ? (
                <Card className="border-none shadow-md">
                  <CardContent className="p-12 text-center">
                    <UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-display font-bold text-slate-700 mb-2">
                      Nenhum convite de usuário
                    </h3>
                    <p className="text-muted-foreground">
                      Gere um convite para que TV/RT criem sua própria conta na plataforma.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {userInvites.map((invite: UserInvite) => {
                    const roleLabel =
                      invite.roles.map((r) => ROLE_LABELS[r] ?? r).join(" / ");
                    const isExpanded = emailingId === invite.id;
                    return (
                      <Card key={invite.id} className="border-none shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-1 flex-wrap">
                                <span className="font-mono font-bold text-lg tracking-wider text-slate-900">
                                  {invite.code}
                                </span>
                                <Badge variant={invite.ativo ? "default" : "secondary"}>
                                  {invite.ativo ? "Ativo" : "Inativo"}
                                </Badge>
                                <Badge variant="outline" className="gap-1">
                                  <Users className="w-3 h-3" /> {roleLabel}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {invite.usos} uso{invite.usos !== 1 ? "s" : ""}
                                {invite.maxUsos ? ` de ${invite.maxUsos}` : ""}
                                {invite.email ? ` · ${invite.email}` : ""}
                                {" · "}
                                {new Date(invite.criadoEm).toLocaleDateString("pt-BR")}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Copy Code */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() =>
                                  copyText(invite.code, `code-${invite.id}`, setCopiedUserCode)
                                }
                              >
                                {copiedUserCode === `code-${invite.id}` ? (
                                  <><CheckCircle className="w-4 h-4 text-green-500" /> Copiado!</>
                                ) : (
                                  <><Copy className="w-4 h-4" /> Código</>
                                )}
                              </Button>
                              {/* Copy Link */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() =>
                                  copyText(
                                    getUserInviteLink(invite.code),
                                    `link-${invite.id}`,
                                    setCopiedUserCode,
                                  )
                                }
                              >
                                {copiedUserCode === `link-${invite.id}` ? (
                                  <><CheckCircle className="w-4 h-4 text-green-500" /> Copiado!</>
                                ) : (
                                  <><LinkIcon className="w-4 h-4" /> Link</>
                                )}
                              </Button>
                              {/* Send Email */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() =>
                                  setEmailingId(isExpanded ? null : invite.id)
                                }
                              >
                                <Mail className="w-4 h-4" /> Email
                              </Button>
                              {/* Delete (only if unused) */}
                              {canCreate && invite.usos === 0 && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-2 text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir convite?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        O código <strong>{invite.code}</strong> será removido
                                        permanentemente. Essa ação não pode ser desfeita.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteUserInviteMutation.mutate(invite.id)}
                                        className="bg-destructive hover:bg-destructive/90"
                                      >
                                        Excluir
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>

                          {/* Email form */}
                          {isExpanded && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="flex gap-2">
                                <Input
                                  type="email"
                                  placeholder="email@destinatario.com"
                                  value={emailTarget[invite.id] ?? invite.email ?? ""}
                                  onChange={(e) =>
                                    setEmailTarget((prev) => ({
                                      ...prev,
                                      [invite.id]: e.target.value,
                                    }))
                                  }
                                  className="bg-white text-sm"
                                />
                                <Button
                                  size="sm"
                                  className="gap-2 shrink-0"
                                  disabled={
                                    sendEmailMutation.isPending ||
                                    !emailTarget[invite.id]?.trim()
                                  }
                                  onClick={() =>
                                    sendEmailMutation.mutate({
                                      id: invite.id,
                                      email: emailTarget[invite.id]!,
                                    })
                                  }
                                >
                                  {sendEmailMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                  Enviar
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* ─── Tab: Empresas (original) ──────────────────────────────── */}
            <TabsContent value="empresas">
              {canCreate && (
                <Card className="mb-8 border-none shadow-md">
                  <CardContent className="p-6">
                    <h2 className="text-lg font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <TicketPlus className="w-5 h-5 text-primary" />
                      Gerar Novo Código
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-4 items-end">
                      <div className="flex-1 space-y-2">
                        <Label>Tipo de Empresa</Label>
                        {isOwner ? (
                          <Select
                            value={tipo}
                            onValueChange={(v) =>
                              setTipo(v as "PARCEIRO_DE_NEGOCIOS" | "VISTORIADORA")
                            }
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PARCEIRO_DE_NEGOCIOS">
                                Parceiro de Negócios
                              </SelectItem>
                              <SelectItem value="VISTORIADORA">Vistoriadora</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="h-10 flex items-center px-3 rounded-md border bg-white text-sm">
                            Parceiro de Negócios
                          </div>
                        )}
                      </div>
                      <div className="w-40 space-y-2">
                        <Label>Máx. de Usos</Label>
                        <Input
                          type="number"
                          placeholder="Ilimitado"
                          value={maxUsos}
                          onChange={(e) => setMaxUsos(e.target.value)}
                          className="bg-white"
                          min={1}
                        />
                      </div>
                      <Button
                        onClick={() => {
                          const data: { tipo: string; maxUsos?: number } = { tipo };
                          if (maxUsos && parseInt(maxUsos) > 0) {
                            data.maxUsos = parseInt(maxUsos);
                          }
                          createCodeMutation.mutate(data);
                        }}
                        disabled={createCodeMutation.isPending}
                        className="gap-2"
                      >
                        {createCodeMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Gerar Código
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {loadingCodes ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : !inviteCodes || inviteCodes.length === 0 ? (
                <Card className="border-none shadow-md">
                  <CardContent className="p-12 text-center">
                    <TicketPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-display font-bold text-slate-700 mb-2">
                      Nenhum código de convite
                    </h3>
                    <p className="text-muted-foreground">
                      Gere um código para convidar seus parceiros de negócios a se cadastrarem.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {inviteCodes.map((invite: InviteCode) => (
                    <Card key={invite.id} className="border-none shadow-sm">
                      <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-mono font-bold text-lg tracking-wider text-slate-900">
                              {invite.code}
                            </span>
                            <Badge variant={invite.ativo ? "default" : "secondary"}>
                              {invite.ativo ? "Ativo" : "Inativo"}
                            </Badge>
                            <Badge variant="outline" className="gap-1">
                              {invite.tipo === "PARCEIRO_DE_NEGOCIOS" ? (
                                <><Building2 className="w-3 h-3" /> Parceiro</>
                              ) : (
                                <><Users className="w-3 h-3" /> Vistoriadora</>
                              )}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {invite.usos} uso{invite.usos !== 1 ? "s" : ""}
                            {invite.maxUsos ? ` de ${invite.maxUsos}` : " (ilimitado)"}
                            {" · "}
                            {new Date(invite.criadoEm).toLocaleDateString("pt-BR")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() =>
                              copyText(
                                getRegistrationLink(invite.code),
                                invite.code,
                                setCopiedCode,
                              )
                            }
                          >
                            {copiedCode === invite.code ? (
                              <><CheckCircle className="w-4 h-4 text-green-500" /> Copiado!</>
                            ) : (
                              <><LinkIcon className="w-4 h-4" /> Copiar Link</>
                            )}
                          </Button>
                          {canCreate && invite.usos === 0 && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-2 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir código?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    O código <strong>{invite.code}</strong> será removido
                                    permanentemente. Essa ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteCodeMutation.mutate(invite.id)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
